import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Truck, LogIn, Eye, EyeOff,
  LayoutDashboard, FileText, Boxes, Wallet,
  Receipt, Shield, Bot, BarChart3
} from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Email and password required');
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r.ok) { toast.success('Welcome back!'); nav('/'); }
      else toast.error(r.message || 'Invalid credentials');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  const features = [
    { icon: LayoutDashboard, title: 'All-in-One Business Hub', desc: 'Manage parties, products, vehicles, and finances from a single dashboard — no more juggling Excel and notebooks.' },
    { icon: FileText,        title: 'GST-Ready Invoicing',     desc: 'Create tax invoices in seconds with automatic GST calculation, HSN codes, and stock updates.' },
    { icon: Boxes,           title: 'Real-Time Stock Control', desc: 'Inventory updates with every purchase and sale. Get instant low-stock alerts so you never run out.' },
    { icon: Truck,           title: 'Fleet & Trip Management', desc: 'Track insurance, fitness, permit, and PUC expiry. Log fuel, maintenance, and trip-wise profitability.' },
    { icon: Wallet,          title: 'Multi-Bank Reconciliation', desc: 'Cash, savings, current, OD, and CC accounts — every payment auto-reconciled in one place.' },
    { icon: Receipt,         title: 'Receivables & Payables',  desc: 'Know exactly who owes you and what you owe — at a glance, on any device.' },
    { icon: BarChart3,       title: 'Real Profit & Loss',      desc: 'Accurate P&L with proper COGS. A balance sheet that actually balances. Decisions on real numbers.' },
    { icon: Bot,             title: 'AI Business Assistant',   desc: 'Ask plain-English questions like "How much did I sell this month?" and get instant answers.' },
  ];

  return (
    <div className="login-shell">
      <style>{loginStyles}</style>

      {/* LEFT — Brand & Value Props */}
      <div className="login-left">
        <div className="login-left-inner">
          <div className="brand-row">
            <div className="brand-icon"><Truck size={28} /></div>
            <div>
              <div className="brand-name">Traders ERP</div>
              <div className="brand-sub">For Trading & Transport Businesses</div>
            </div>
          </div>

          <h1 className="hero-title">
            Run Your Entire Trading & Transport Business from <span className="hl">One Dashboard</span>
          </h1>
          <p className="hero-sub">
            Buy. Sell. Deliver. Track every rupee. The complete ERP built for traders who move their own goods.
          </p>

          <div className="features-grid">
            {features.map((f, i) => (
              <div className="feature-card" key={i}>
                <div className="feature-icon"><f.icon size={18} /></div>
                <div>
                  <div className="feature-title">{f.title}</div>
                  <div className="feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="trust-line">
            <span><Shield size={14} /> Bank-grade security</span>
            <span>📱 Works on any device</span>
            <span>⚡ Setup in 15 minutes</span>
            <span>🇮🇳 GST compliant</span>
          </div>
        </div>
      </div>

      {/* RIGHT — Login form */}
      <div className="login-right">
        <div className="login-card">
          <div className="form-header">
            <h2>Welcome Back</h2>
            <p>Log in to manage your business</p>
          </div>

          <form onSubmit={submit} className="login-form">
            <label>Email Address</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />

            <label>Password</label>
            <div className="pwd-wrap">
              <input
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                required
              />
              <button type="button" className="eye-btn" onClick={() => setShowPwd(s => !s)}>
                {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Signing in…' : (<><LogIn size={16}/> Sign In</>)}
            </button>
          </form>

          <div className="form-footer">
            <span>New here?</span> <Link to="/register">Create an account</Link>
          </div>

          <div className="form-footer-small">
            Each business gets its own private workspace — your data stays yours.
          </div>
        </div>
      </div>
    </div>
  );
}

const loginStyles = `
.login-shell { min-height:100vh; display:grid; grid-template-columns: 1.15fr 1fr; background:#f8fafc; }
@media (max-width: 980px) { .login-shell { grid-template-columns: 1fr; } }

.login-left {
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%);
  color: #fff;
  padding: 50px 50px 40px;
  display: flex; align-items: center;
  position: relative; overflow: hidden;
}
.login-left::before {
  content: ''; position: absolute; top: -120px; right: -120px;
  width: 360px; height: 360px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.15), transparent 70%);
}
.login-left::after {
  content: ''; position: absolute; bottom: -150px; left: -100px;
  width: 400px; height: 400px; border-radius: 50%;
  background: radial-gradient(circle, rgba(96,165,250,0.18), transparent 70%);
}
.login-left-inner { position: relative; z-index: 1; max-width: 620px; width: 100%; margin: auto; }

.brand-row { display: flex; align-items: center; gap: 14px; margin-bottom: 36px; }
.brand-icon {
  width: 50px; height: 50px; border-radius: 12px;
  background: rgba(255,255,255,0.15);
  display: grid; place-items: center;
  border: 1px solid rgba(255,255,255,0.25);
}
.brand-name { font-size: 22px; font-weight: 700; line-height: 1.1; }
.brand-sub { font-size: 12px; opacity: 0.75; margin-top: 2px; }

.hero-title { font-size: 32px; font-weight: 800; line-height: 1.18; margin: 0 0 14px; }
.hero-title .hl { color: #fcd34d; }
.hero-sub { font-size: 15px; opacity: 0.88; margin: 0 0 30px; line-height: 1.55; }

.features-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
}
@media (max-width: 600px) { .features-grid { grid-template-columns: 1fr; } }

.feature-card {
  display: flex; gap: 12px; align-items: flex-start;
  background: rgba(255,255,255,0.08);
  padding: 12px 14px; border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.1);
  transition: background .15s;
}
.feature-card:hover { background: rgba(255,255,255,0.13); }
.feature-icon {
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  background: rgba(252,211,77,0.18);
  color: #fcd34d;
  display: grid; place-items: center;
}
.feature-title { font-size: 13px; font-weight: 600; margin-bottom: 2px; }
.feature-desc { font-size: 11.5px; opacity: 0.82; line-height: 1.45; }

.trust-line {
  margin-top: 28px; padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.15);
  display: flex; flex-wrap: wrap; gap: 14px 22px;
  font-size: 12px; opacity: 0.85;
}
.trust-line span { display: inline-flex; align-items: center; gap: 6px; }

/* RIGHT */
.login-right {
  display: grid; place-items: center;
  padding: 40px 24px;
}
.login-card {
  background: #fff; width: 100%; max-width: 420px;
  border-radius: 16px; padding: 38px 34px;
  box-shadow: 0 4px 30px rgba(15,23,42,0.07);
  border: 1px solid #e2e8f0;
}
.form-header { margin-bottom: 26px; }
.form-header h2 { font-size: 26px; font-weight: 700; margin: 0 0 6px; color: #0f172a; }
.form-header p  { font-size: 14px; color: #64748b; margin: 0; }

.login-form label {
  display: block; font-size: 12px; font-weight: 600; color: #334155;
  margin: 14px 0 6px; text-transform: uppercase; letter-spacing: 0.4px;
}
.login-form input {
  width: 100%; padding: 11px 13px; font-size: 14px;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  background: #f8fafc; color: #0f172a;
  transition: border-color .15s, background .15s;
  box-sizing: border-box;
}
.login-form input:focus {
  outline: none; border-color: #1d4ed8; background: #fff;
  box-shadow: 0 0 0 3px rgba(29,78,216,0.12);
}
.pwd-wrap { position: relative; }
.eye-btn {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #64748b;
  padding: 6px; display: grid; place-items: center;
}
.eye-btn:hover { color: #1e40af; }

.btn-login {
  width: 100%; margin-top: 22px;
  padding: 12px 16px; font-size: 14px; font-weight: 600;
  background: linear-gradient(135deg, #1e40af, #1d4ed8);
  color: #fff; border: none; border-radius: 8px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  transition: transform .12s, box-shadow .12s;
}
.btn-login:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(29,78,216,0.35);
}
.btn-login:disabled { opacity: 0.6; cursor: not-allowed; }

.form-footer { text-align: center; margin-top: 20px; font-size: 13px; color: #475569; }
.form-footer a { color: #1d4ed8; font-weight: 600; text-decoration: none; }
.form-footer a:hover { text-decoration: underline; }
.form-footer-small {
  margin-top: 18px; padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
  font-size: 11.5px; color: #94a3b8; text-align: center; line-height: 1.5;
}
`;
